"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCart } from "@/components/CartProvider";
import { useUserLists } from "@/components/UserListsProvider";

function HomeIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 10.6 12 4l8 6.6V20H4z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />
      <path d="M9.5 20v-5h5v5" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function CatalogIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M5 7h14M5 12h14M5 17h14" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M4 6h2l1.4 7.2a1 1 0 0 0 1 .8h8.8a1 1 0 0 0 1-.8L19.8 8H7.2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <circle cx="10" cy="18" r="1.2" fill="currentColor" />
      <circle cx="17" cy="18" r="1.2" fill="currentColor" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M12 20.2 4.9 13.4a4.9 4.9 0 0 1 6.9-7l.2.2.2-.2a4.9 4.9 0 0 1 6.9 7Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5.5 19a6.5 6.5 0 0 1 13 0"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

type NavItem = {
  href: string;
  label: string;
  icon: ReactNode;
  count?: number;
};

export function MobileBottomNav() {
  const pathname = usePathname();
  const { totalQty } = useCart();
  const { favoriteCount, isAuthenticated } = useUserLists();

  if (pathname.startsWith("/admin") || pathname === "/account/login" || pathname === "/account/register") {
    return null;
  }

  const items: NavItem[] = [
    { href: "/", label: "Главная", icon: <HomeIcon /> },
    { href: "/catalog", label: "Каталог", icon: <CatalogIcon /> },
    { href: "/cart", label: "Корзина", icon: <CartIcon />, count: totalQty > 0 ? totalQty : undefined },
    {
      href: isAuthenticated ? "/favorites" : "/account/login?next=%2Ffavorites",
      label: "Избранное",
      icon: <HeartIcon />,
      count: isAuthenticated && favoriteCount > 0 ? favoriteCount : undefined
    },
    { href: isAuthenticated ? "/account" : "/account/login", label: "Профиль", icon: <ProfileIcon /> }
  ];

  return (
    <nav aria-label="Мобильная навигация" className="mobile-bottom-nav">
      {items.map((item) => {
        const isActive = item.href === "/" ? pathname === "/" : pathname === item.href || pathname.startsWith(`${item.href}/`);

        return (
          <Link
            className={`mobile-bottom-nav__item ${isActive ? "mobile-bottom-nav__item--active" : ""}`}
            href={item.href}
            key={item.label}
          >
            <span className="mobile-bottom-nav__icon">
              {item.icon}
              {item.count ? <span className="mobile-bottom-nav__count">{item.count}</span> : null}
            </span>
            <span className="mobile-bottom-nav__label">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
